// src/utils/measurement-profiles.js
// Catálogo de plantillas de medición.
// - "general": sin plantilla (unidad libre).
// - "dimensional"/"electrical"/"force": definen unidad/escala. RANGOS EN ELÉCTRICA -> VACÍOS.

export const PROFILES = {
  // ============================
  // Dimensional — Longitud
  // ============================
  'DIM-LONG:caliper:v1': {
    family: 'dimensional',
    subfamily: 'longitud',
    label: 'Calibre (pie de rey)',
    unit: { base: 'mm', scales: ['mm'] },
    ranges: [{ label: '0–150 mm', min: 0, max: 150 }], // OK conservar (opcional)
    suggestSetpoints: ({ min, max }) => [1, 5, 10, 30, 50, 100]
      .filter(value => value >= min && value <= max),
    ui: { simple: true, advanced: true },
  },

  'DIM-LONG:micrometer:v1': {
    family: 'dimensional',
    subfamily: 'longitud',
    label: 'Micrómetro',
    unit: { base: 'mm', scales: ['mm', 'µm'] },
    ranges: [{ label: '0–25 mm', min: 0, max: 25 }],
    suggestSetpoints: ({ min, max }) => {
      const span = max - min;
      return [0, 0.2, 0.4, 0.6, 0.8, 1].map(p => +(min + span * p).toFixed(3));
    },
    ui: { simple: true, advanced: true },
  },

  'DIM-LONG:rule:v1': {
    family: 'dimensional',
    subfamily: 'longitud',
    label: 'Regla / Flexómetro',
    unit: { base: 'mm', scales: ['mm'] },
    ranges: [{ label: '0–3000 mm', min: 0, max: 3000 }],
    suggestSetpoints: ({ min, max }) => {
      const span = max - min;
      return [0, 0.2, 0.4, 0.6, 0.8, 1].map(p => +(min + span * p).toFixed(0));
    },
    ui: { simple: true, advanced: true },
  },

  'DIM-LONG:depth:v1': {
    family: 'dimensional',
    subfamily: 'longitud',
    label: 'Calibre de profundidad',
    unit: { base: 'mm', scales: ['mm'] },
    ranges: [{ label: '0–200 mm', min: 0, max: 200 }],
    suggestSetpoints: ({ min, max }) => {
      const span = max - min;
      return [0, 0.25, 0.5, 0.75, 1].map(p => +(min + span * p).toFixed(2));
    },
    ui: { simple: true, advanced: true },
  },

  // ============================
  // Eléctrica — Multímetro (SIN ranges fijos)
  // ============================

  // Voltaje DC
  'ELEC:multimeter:V:DC:v1': {
    family: 'electrical',
    label: 'Multímetro — Voltaje DC',
    mode: 'V',
    submode: 'DC',
    unit: { base: 'V', scales: ['mV', 'V'] },
    ranges: [],              // <— rangos variables: el técnico escribe libre
    ui: { simple: true, advanced: true },
  },

  // Voltaje AC
  'ELEC:multimeter:V:AC:v1': {
    family: 'electrical',
    label: 'Multímetro — Voltaje AC',
    mode: 'V',
    submode: 'AC',
    unit: { base: 'V', scales: ['mV', 'V'] },
    ranges: [],              // <— sin ranges fijos
    ui: { simple: true, advanced: true },
  },

  // Corriente DC
  'ELEC:multimeter:A:DC:v1': {
    family: 'electrical',
    label: 'Multímetro — Corriente DC',
    mode: 'A',
    submode: 'DC',
    unit: { base: 'A', scales: ['mA', 'A'] },
    ranges: [],              // <— sin ranges fijos
    ui: { simple: true, advanced: true },
  },

  // Corriente AC
  'ELEC:multimeter:A:AC:v1': {
    family: 'electrical',
    label: 'Multímetro — Corriente AC',
    mode: 'A',
    submode: 'AC',
    unit: { base: 'A', scales: ['mA', 'A'] },
    ranges: [],              // <— sin ranges fijos
    ui: { simple: true, advanced: true },
  },

  // Resistencia
  'ELEC:multimeter:R:v1': {
    family: 'electrical',
    label: 'Multímetro — Resistencia',
    mode: 'Ω',
    unit: { base: 'Ω', scales: ['Ω', 'kΩ', 'MΩ'] },
    ranges: [],              // <— sin ranges fijos
    ui: { simple: true, advanced: true },
  },

  // Frecuencia
  'ELEC:multimeter:Hz:v1': {
    family: 'electrical',
    label: 'Multímetro — Frecuencia',
    mode: 'Hz',
    unit: { base: 'Hz', scales: ['Hz', 'kHz'] },
    ranges: [],              // <— sin ranges fijos
    ui: { simple: true, advanced: true },
  },

  // ============================
  // Fuerza / Torque
  // ============================
  'FORCE:dynamometer:v1': {
    family: 'force',
    subfamily: 'fuerza',
    label: 'Dinamómetro',
    unit: { base: 'N', scales: ['N', 'kN'] },
    ranges: [{ label: '0–500 N', min: 0, max: 500 }],
    suggestSetpoints: ({ min, max }) => {
      const span = max - min;
      return [0, 0.25, 0.5, 0.75, 1].map(p => +(min + span * p).toFixed(1));
    },
    ui: { simple: true, advanced: true },
  },

  'FORCE:torque_wrench:v1': {
    family: 'force',
    subfamily: 'torque',
    label: 'Torquímetro',
    unit: { base: 'Nm', scales: ['Nm', 'lbf·ft'] },
    ranges: [{ label: '0–200 Nm', min: 0, max: 200 }],
    suggestSetpoints: ({ min, max }) => {
      const span = max - min;
      return [0, 0.25, 0.5, 0.75, 1].map(p => +(min + span * p).toFixed(1));
    },
    ui: { simple: true, advanced: true },
  },

  'FORCE:load_cell:v1': {
    family: 'force',
    subfamily: 'fuerza',
    label: 'Célula de carga',
    unit: { base: 'N', scales: ['N', 'kN', 'kgf'] },
    ranges: [{ label: '0–20 kN', min: 0, max: 20000 }],
    suggestSetpoints: ({ min, max }) => {
      const span = max - min;
      return [0, 0.2, 0.4, 0.6, 0.8, 1].map(p => +(min + span * p).toFixed(0));
    },
    ui: { simple: true, advanced: true },
  },
};

export const PROFILE_GROUPS = {
  general: [],
  dimensional: ['DIM-LONG:caliper:v1', 'DIM-LONG:micrometer:v1', 'DIM-LONG:rule:v1', 'DIM-LONG:depth:v1'],
  force: ['FORCE:dynamometer:v1', 'FORCE:torque_wrench:v1', 'FORCE:load_cell:v1'],
  electrical: [
    'ELEC:multimeter:V:DC:v1',
    'ELEC:multimeter:V:AC:v1',
    'ELEC:multimeter:A:DC:v1',
    'ELEC:multimeter:A:AC:v1',
    'ELEC:multimeter:R:v1',
    'ELEC:multimeter:Hz:v1',
  ],
};
